import { FunctionComponent } from 'react';

import { Adg4Tabs } from '@/components/adg4/tabs/src/tabs';
import { Megaphone } from '@/libraries/wpl/foundations/src/icons/software/megaphone';
import { Notepad } from '@/libraries/wpl/foundations/src/icons/software/notepad';
import { Rocket } from '@/libraries/wpl/foundations/src/icons/software/rocket';
import { ContentTypeVideo } from '@/templates/software/confluence/confluence-software-page/src/sections/content-types/content-type-video';

import { MindMap as MindMapAnimation } from './animations/MindMap';
import { OrgChart as OrgChartAnimation } from './animations/OrgChart';
import { Page as PageAnimation } from './animations/Page';
import { ProjectPlan as ProjectPlanAnimation } from './animations/ProjectPlan';
import { Spreadsheet as SpreadsheetAnimation } from './animations/Spreadsheet';
import { Whiteboard as WhiteboardAnimation } from './animations/Whiteboard';
import { Wiki as WikiAnimation } from './animations/Wiki';

interface VideoData {
	videoSrcMp4: string;
	videoSrcWebm: string;
	videoPreviewSrc: string;
}

const databaseVideoData: VideoData = {
	videoSrcMp4:
		'https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Databases_LowBR_reencode.mp4',
	videoSrcWebm:
		'https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Databases_LowBR.webm',
	videoPreviewSrc:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/PT_CT_Databases_Preview.webp',
};

const whiteboardsVideoData: VideoData = {
	videoSrcMp4:
		'https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Whiteboards_LowBR_reencode.mp4',
	videoSrcWebm:
		'https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Whiteboards_LowBR.webm',
	videoPreviewSrc:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/PT_CT_Whiteboards_Preview.webp',
};

const pagesVideoData: VideoData = {
	videoSrcMp4:
		'https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Pages_LowBR_reencode.mp4',
	videoSrcWebm:
		'https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Pages_LowBR.webm',
	videoPreviewSrc:
		'https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/PT_CT_Pages_Preview.webp',
};
interface SubAnchorData {
	pageId: string;
	video: VideoData;
	animation: FunctionComponent;
}

const subAnchorMap: Record<string, SubAnchorData> = {
	brainstorm: {
		pageId: 'paid-lp-conf-sub-anchor-brainstorm',
		video: whiteboardsVideoData,
		animation: MindMapAnimation,
	},
	'business plan': {
		pageId: 'paid-lp-conf-sub-anchor-business-plan',
		video: whiteboardsVideoData,
		animation: PageAnimation,
	},
	coda: {
		pageId: 'paid-lp-conf-keywords-competitors',
		video: pagesVideoData,
		animation: PageAnimation,
	},
	'collaboration tool': {
		pageId: 'paid-lp-conf-sub-anchor-collaboration-tools',
		video: pagesVideoData,
		animation: PageAnimation,
	},
	'documentation tools': {
		pageId: 'paid-lp-conf-keywords-documentation',
		video: pagesVideoData,
		animation: PageAnimation,
	},
	'google docs': {
		pageId: 'paid-lp-conf-keywords-competitors',
		video: pagesVideoData,
		animation: PageAnimation,
	},
	'knowledge management': {
		pageId: 'paid-lp-conf-sub-anchor-knowledge-management',
		video: pagesVideoData,
		animation: WikiAnimation,
	},
	'microsoft sharepoint': {
		pageId: 'paid-lp-conf-keywords-competitors',
		video: pagesVideoData,
		animation: PageAnimation,
	},
	'microsoft teams': {
		pageId: 'paid-lp-conf-keywords-competitors',
		video: pagesVideoData,
		animation: PageAnimation,
	},
	notion: {
		pageId: 'paid-lp-conf-keywords-competitors',
		video: pagesVideoData,
		animation: PageAnimation,
	},
	'organization chart': {
		pageId: 'paid-lp-conf-sub-anchor-organization-chart',
		video: whiteboardsVideoData,
		animation: OrgChartAnimation,
	},
	'project management software': {
		pageId: 'paid-lp-conf-sub-anchor-project-plan',
		video: pagesVideoData,
		animation: ProjectPlanAnimation,
	},
	'project management templates': {
		pageId: 'paid-lp-conf-sub-anchor-project-plan',
		video: pagesVideoData,
		animation: ProjectPlanAnimation,
	},
	'project plan': {
		pageId: 'paid-lp-conf-keywords-project-plan',
		video: pagesVideoData,
		animation: ProjectPlanAnimation,
	},
	spreadsheet: {
		pageId: 'paid-lp-conf-keywords-spreadsheets',
		video: databaseVideoData,
		animation: SpreadsheetAnimation,
	},
	'task management': {
		pageId: 'paid-lp-conf-keywords-task-list',
		video: pagesVideoData,
		animation: SpreadsheetAnimation,
	},
	whiteboard: {
		pageId: 'paid-lp-conf-sub-anchor-whiteboard',
		video: whiteboardsVideoData,
		animation: WhiteboardAnimation,
	},
	wiki: {
		pageId: 'paid-lp-conf-sub-anchor-wiki',
		video: pagesVideoData,
		animation: WikiAnimation,
	},
	'word editor': {
		pageId: 'paid-lp-conf-keywords-documentation',
		video: pagesVideoData,
		animation: PageAnimation,
	},
};

export const isTargetedSubAnchor = (subAnchor = '') => !!subAnchorMap[subAnchor];

// CFIND-3233 supported subAnchors
export const isInProductJourneySubAnchor = (subAnchor = '') =>
	[
		'brainstorm',
		'business plan',
		'coda',
		'collaboration tool',
		'documentation tools',
		'google docs',
		'knowledge management',
		'microsoft sharepoint',
		'microsoft teams',
		'notion',
		'online flowchart',
		'organization chart',
		'project management templates',
		'task management',
		'whiteboard',
		'wiki',
	].includes(subAnchor);

export const isInConfluenceCBSubAnchorCMSVariant = (variant = '') =>
	[
		'paid-lp-confl-template',
		'paid-lp-confl-documentation',
		'paid-lp-confl-projectmgmt',
		'anchor-lp-conf-collaboration',
		'anchor-lp-conf-automation',
		'anchor-lp-conf-teamwork',
		'anchor-lp-conf-competitors',
		'anchor-lp-conf-work-management',
	].includes(variant);

export const isInContextualBanditsSubAnchor = (subAnchor = '') =>
	[
		'analytics tools',
		'brainstorm',
		'business plan',
		'coda',
		'collaboration tools',
		'communication system',
		'content management',
		'digital diagram',
		'documentation tools',
		'file sharing',
		'gantt chart',
		'google docs',
		'intranet',
		'knowledge management',
		'organization chart',
		'other templates',
		'others',
		'process management',
		'project management software',
		'project management templates',
		'project plan',
		'project reporting',
		'remote',
		'spreadsheet',
		'task management',
		'team management',
		'time management',
		'whiteboard',
		'wiki',
		'word editor',
		'work management',
		'workflow',
	].includes(subAnchor);

export const getPageId = (subAnchor = '') =>
	subAnchorMap[subAnchor]?.pageId || 'paid-lp-conf-keywords-documentation';

export const generateTabComponentSubAnchor = (
	keyword = '',
	tab: {
		title: string;
		description: string;
		content: string;
	},
) => {
	const videoProps = subAnchorMap[keyword]?.video || pagesVideoData;

	return (
		<Adg4Tabs>
			<ContentTypeVideo
				{...videoProps}
				showBackground={true}
				active={true}
				showAsterisks={true}
				hideSeparator={true}
				title={tab.title}
				description={tab.description}
				iconCollection={Array.from(
					new DOMParser().parseFromString(tab.content, 'text/html').querySelectorAll('p'),
				)
					.slice(0, 3)
					.map((p, i) => ({
						description: p.textContent || '',
						icon: [
							<Rocket key="Rocket" size="small" />,
							<Megaphone key="Megaphone" size="small" />,
							<Notepad key="Notepad" size="small" />,
						][i],
					}))}
			/>
		</Adg4Tabs>
	);
};

export const getAnimationBySubAnchor = (subAnchor: string) => {
	const riveAnimation = subAnchorMap[subAnchor]?.animation;
	return riveAnimation ? { riveAnimation } : undefined;
};
